import { useEffect, useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowLeft, Download, LoaderCircle } from 'lucide-react';
import baseapi from '../../baseapi';

type MinutePoint = { minute: number; time: string; count: number };
type DayData = { date: string; total: number | null; counted_total: number; manual_adjustment_total: number; points: MinutePoint[] };
type MinuteSeries = { date: string; source: string; points: MinutePoint[]; counted_total: number; manual_adjustment_total?: number };
const IST_OFFSET_SECONDS = 5 * 60 * 60 + 30 * 60;
const dateInIst = (epochSeconds: number) => new Date((epochSeconds + IST_OFFSET_SECONDS) * 1000).toISOString().slice(0, 10);
const shiftDate = (day: string, offset: number) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};
const number = (v: number | null | undefined) => v == null ? '—' : Math.round(v).toLocaleString('en-IN');
const dateLabel = (v: string) => new Date(`${v}T00:00:00Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const colors = ['#e6781b', '#4d8dba', '#8a57b5', '#178a67'];

export function DarshanCompletedDetails({ onClose }: { onClose: () => void }) {
  const [days, setDays] = useState<DayData[]>([]);
  const [range, setRange] = useState<1 | 2 | 4>(2);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError('');
    const load = async () => {
      const today = dateInIst(Date.now() / 1000);
      const dates = Array.from({ length: range }, (_, i) => shiftDate(today, -i));
      const results = await Promise.allSettled(dates.map(date =>
        baseapi.get<MinuteSeries>('dashboard/darshan-completed/minutes', { params: { day: date }, timeout: 30000 })
      ));
      const loaded = results.flatMap((result, index) => {
        if (result.status !== 'fulfilled') return [];
        const series = result.value.data;
        const points = series?.points || [];
        const countedTotal = series?.counted_total ?? points.reduce((sum, p) => sum + p.count, 0);
        return [{ date: series?.date || dates[index], total: null, counted_total: countedTotal,
          manual_adjustment_total: series?.manual_adjustment_total || 0, points } satisfies DayData];
      });
      if (!loaded.length) throw new Error('No minute series could be loaded');
      if (alive) {
        setDays(loaded);
        setBusy(false);
      }

      // Published daily totals come from a slower dashboard snapshot. Load them after the chart
      // and minute table are visible so a slow summary request cannot hold the whole page blank.
      await Promise.all(loaded.map(async day => {
        try {
          const summary = await baseapi.get(`dashboard/days/${day.date}`, { timeout: 30000 });
          const inHill = summary.data?.kpis?.in_hill;
          const total = inHill?.source === 'camera' && typeof inHill.out === 'number' ? inHill.out : null;
          if (alive) setDays(current => current.map(item => item.date === day.date ? { ...item, total } : item));
        } catch {
          // The camera-counted series is still useful when the published-total endpoint is unavailable.
        }
      }));
    };
    load()
      .catch(() => { if (alive) setError('Could not load Darshan Completed minute counts. Please try again.'); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [range]);

  const chart = useMemo(() => {
    if (!days.length) return [];
    return Array.from({ length: 1440 }, (_, minute) => {
      const point: Record<string, number | string> = { minute, time: `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}` };
      days.forEach((day, i) => { point[`day${i}`] = day.points[minute]?.count ?? 0; });
      return point;
    });
  }, [days]);

  const downloadExcelCsv = () => {
    if (!days.length) return;
    const rows = [
      ['Minute (IST)', ...days.map(day => day.date)],
      ...chart.map(p => [String(p.time), ...days.map((_, i) => String(p[`day${i}`] ?? 0))]),
      ['Camera-counted total', ...days.map(day => String(day.counted_total))],
      ['Published daily total', ...days.map(day => String(day.total ?? ''))],
    ];
    const csv = '\uFEFF' + rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `darshan-completed-${days[days.length - 1].date}-to-${days[0].date}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <main className="fixed inset-0 z-[100] overflow-hidden bg-[#f4f5f8] p-2 sm:p-5 dark:bg-[#111215]">
      <section aria-labelledby="darshan-completed-title" className="mx-auto flex h-full w-full flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl dark:border-[#303541] dark:bg-[#11151d]">
        <header className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3 border-b border-neutral-200 dark:border-[#303541]">
          <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-bold text-neutral-800 hover:bg-neutral-100 dark:border-[#303541] dark:text-neutral-100 dark:hover:bg-[#202631]">
            <ArrowLeft className="h-4 w-4" /> Back to Overview
          </button>
          <div className="min-w-0 flex-1">
            <h2 id="darshan-completed-title" className="text-lg sm:text-xl font-black text-neutral-900 dark:text-white">Darshan Completed · minute by minute</h2>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">Last {range} {range === 1 ? 'day' : 'days'} · Asia/Kolkata</p>
          </div>
          <div className="flex rounded-lg bg-neutral-100 dark:bg-[#202631] p-1" aria-label="Date range">
            {([1, 2, 4] as const).map(value => <button key={value} type="button" onClick={() => setRange(value)} aria-pressed={range === value} className={`rounded-md px-3 py-1.5 text-xs font-bold ${range === value ? 'bg-white dark:bg-[#343c4a] text-emerald-700 dark:text-emerald-300 shadow' : 'text-neutral-600 dark:text-neutral-300'}`}>{value} {value === 1 ? 'day' : 'days'}</button>)}
          </div>
          <button type="button" onClick={downloadExcelCsv} disabled={!days.length} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50 hover:bg-emerald-800">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download Excel</span><span className="sm:hidden">Excel</span>
          </button>
        </header>

        {busy ? <div className="flex min-h-64 items-center justify-center gap-2 text-neutral-600 dark:text-neutral-300"><LoaderCircle className="h-5 w-5 animate-spin" /> Loading minute counts…</div> : error ? <div className="p-8 text-center font-semibold text-rose-600">{error}</div> : days.length > 0 && <>
          <div className={`grid grid-cols-2 ${days.length > 2 ? 'xl:grid-cols-4' : ''} gap-2 px-4 sm:px-6 pt-3`}>
            {days.map((day, i) => <div key={day.date} className="rounded-xl border border-neutral-200 dark:border-[#303541] bg-neutral-50 dark:bg-[#191e28] p-3">
              <div className="text-xs font-bold uppercase tracking-wide text-neutral-500 dark:text-neutral-400" style={{ color: colors[i] }}>{dateLabel(day.date)}</div>
              <div className="mt-1 text-xl sm:text-2xl font-black text-neutral-900 dark:text-white">{number(day.total)} <span className="text-xs font-semibold text-neutral-500">published total</span></div>
              <div className="text-xs text-neutral-600 dark:text-neutral-400">Per-minute counts{day.manual_adjustment_total ? ' incl. manual correction' : ' from cameras'}: {number(day.counted_total)}</div>
            </div>)}
          </div>

          <div className="h-64 shrink-0 px-2 sm:px-5 pt-3" aria-label="Darshan Completed per-minute area chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 5, right: 12, left: 2, bottom: 2 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" opacity={0.25} />
                <XAxis dataKey="time" interval={119} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                {days.map((day, i) => <Area key={day.date} type="monotone" dataKey={`day${i}`} name={dateLabel(day.date)} stroke={colors[i]} fill={colors[i]} fillOpacity={0.16} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />)}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="mx-4 sm:mx-6 mb-4 min-h-0 flex-1 overflow-auto rounded-xl border border-neutral-200 dark:border-[#303541]">
            <table className="w-full min-w-[520px] table-fixed border-collapse text-xs sm:text-sm">
              <colgroup>
                <col style={{ width: '22%' }} />
                {days.map(day => <col key={day.date} style={{ width: `${78 / days.length}%` }} />)}
              </colgroup>
              <thead className="sticky top-0 z-10 bg-neutral-100 dark:bg-[#202631] text-neutral-700 dark:text-neutral-200">
                <tr><th className="px-3 py-2.5 text-left">Minute (IST)</th>{days.map((day, i) => <th key={day.date} className="px-2 py-2.5 text-center font-bold" style={{ color: colors[i] }}>{dateLabel(day.date)}</th>)}</tr>
              </thead>
              <tbody>
                {chart.map(p => <tr key={p.minute} className="border-t border-neutral-100 dark:border-[#282e39] odd:bg-white even:bg-neutral-50 dark:odd:bg-[#11151d] dark:even:bg-[#171c25]">
                  <td className="px-3 py-1 font-semibold tabular-nums text-neutral-800 dark:text-neutral-100">{p.time}</td>{days.map((day, i) => <td key={day.date} className="px-2 py-1 text-center font-medium tabular-nums text-neutral-700 dark:text-neutral-200">{number(Number(p[`day${i}`]))}</td>)}
                </tr>)}
              </tbody>
            </table>
          </div>
          <p className="px-4 sm:px-6 pb-3 text-[11px] text-neutral-500 dark:text-neutral-400">Minute values are non-negative net counts from configured Darshan Completed camera lines, plus any documented manual corrections.</p>
        </>}
      </section>
    </main>
  );
}
